import React, { useEffect, useState } from "react";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import {
    Loader2,
    Sparkles,
    RefreshCw,
    Download,
    FileText,
    FileSpreadsheet,
    CalendarDays,
    Clock,
    Info,
    Mail,
    Bell,
    History,
    LightbulbIcon,
    ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import WhatsAppReportSection from "@/components/WhatsAppReportSection";
import { toast } from "sonner";

const SECTIONS = [
    { key: "what_happened", label: "What happened?" },
    { key: "why", label: "Why?" },
    { key: "needs_attention", label: "Needs attention" },
    { key: "what_improved", label: "What improved" },
    { key: "what_next", label: "What next?" },
];

const PERIODS = [
    { v: "daily", l: "Daily" },
    { v: "weekly", l: "Weekly" },
    { v: "monthly", l: "Monthly" },
];

const CADENCES = [
    { v: "daily_09", l: "Daily · 9:00 AM" },
    { v: "weekly_monday_09", l: "Weekly · Mondays 9:00 AM" },
    { v: "monthly_1st_09", l: "Monthly · 1st at 9:00 AM" },
];

export default function Reports() {
    const { currentBusiness } = useApp();
    const [period, setPeriod] = useState("weekly");
    const [report, setReport] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [regen, setRegen] = useState(false);
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState(null);
    const [asking, setAsking] = useState(false);
    const [schedules, setSchedules] = useState([]);
    const [scheduleLabel, setScheduleLabel] = useState("");
    const [scheduleForm, setScheduleForm] = useState({
        cadence: "weekly_monday_09",
        recipients: "",
        enabled: false,
    });
    const [savingSched, setSavingSched] = useState(false);

    const load = async () => {
        if (!currentBusiness) return;
        setLoading(true);
        setAnswer(null);
        setQuestion("");
        try {
            const [{ report: r }, { reports }, sched] = await Promise.all([
                api.periodReport(currentBusiness.id, period),
                api.listPeriodReports(currentBusiness.id, period),
                api.reportSchedules(currentBusiness.id),
            ]);
            setReport(r);
            setHistory(reports || []);
            setSchedules(sched.schedules || []);
            setScheduleLabel(sched.demo_label || "");
            const existing = (sched.schedules || []).find((s) => s.period === period);
            if (existing) {
                setScheduleForm({
                    cadence: existing.cadence,
                    recipients: (existing.recipients || []).join(", "),
                    enabled: !!existing.enabled,
                });
            } else {
                setScheduleForm({
                    cadence:
                        period === "daily"
                            ? "daily_09"
                            : period === "monthly"
                            ? "monthly_1st_09"
                            : "weekly_monday_09",
                    recipients: "",
                    enabled: false,
                });
            }
        } catch {
            toast.error("Couldn't load report.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBusiness?.id, period]);

    const regenerate = async () => {
        setRegen(true);
        try {
            const { report: r } = await api.genPeriodReport(currentBusiness.id, period);
            setReport(r);
            const { reports } = await api.listPeriodReports(currentBusiness.id, period);
            setHistory(reports || []);
            toast.success(`Fresh ${period} report generated.`);
        } catch {
            toast.error("Regeneration failed.");
        } finally {
            setRegen(false);
        }
    };

    const ask = async () => {
        if (!question.trim() || !report) return;
        setAsking(true);
        setAnswer(null);
        try {
            const res = await api.askReport(report.id, question);
            setAnswer(res.answer);
        } catch {
            toast.error("AI couldn't answer.");
        } finally {
            setAsking(false);
        }
    };

    const download = async (kind) => {
        if (!report) return;
        try {
            await api.downloadReport(report.id, kind);
            toast.success(`${kind.toUpperCase()} downloaded.`);
        } catch {
            toast.error("Download failed.");
        }
    };

    const saveSchedule = async () => {
        setSavingSched(true);
        try {
            const recipients = scheduleForm.recipients
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
            const res = await api.saveReportSchedule(currentBusiness.id, {
                period,
                cadence: scheduleForm.cadence,
                recipients,
                enabled: scheduleForm.enabled,
            });
            toast.success("Schedule saved.");
            setScheduleLabel(res.demo_label || scheduleLabel);
            load();
        } catch {
            toast.error("Save failed.");
        } finally {
            setSavingSched(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-6 fade-in" data-testid="reports-page">
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="label-eyebrow text-cyan-300">Reports</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-slate-50 tracking-tight">
                        Business reports
                    </h1>
                    <p className="mt-2 text-sm text-slate-400">
                        Daily · Weekly · Monthly — grounded on your reviews, KPIs and complaints.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={() => download("pdf")}
                        disabled={!report}
                        data-testid="report-download-pdf"
                        variant="outline"
                        className="border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-200 gap-2"
                    >
                        <FileText className="h-4 w-4" /> PDF
                    </Button>
                    <Button
                        onClick={() => download("csv")}
                        disabled={!report}
                        data-testid="report-download-csv"
                        variant="outline"
                        className="border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-200 gap-2"
                    >
                        <FileSpreadsheet className="h-4 w-4" /> CSV
                    </Button>
                    <Button
                        onClick={regenerate}
                        disabled={regen}
                        data-testid="report-regenerate-button"
                        className="bg-indigo-500 hover:bg-indigo-400 text-white gap-2"
                    >
                        {regen ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4" />
                        )}
                        Regenerate
                    </Button>
                </div>
            </div>

            <Tabs value={period} onValueChange={setPeriod}>
                <TabsList
                    data-testid="report-period-tabs"
                    className="bg-white/[0.03] border border-white/10"
                >
                    {PERIODS.map((p) => (
                        <TabsTrigger
                            key={p.v}
                            value={p.v}
                            data-testid={`report-period-${p.v}`}
                        >
                            <CalendarDays className="h-3.5 w-3.5 mr-1.5" />
                            {p.l}
                        </TabsTrigger>
                    ))}
                </TabsList>

                {PERIODS.map((p) => (
                    <TabsContent
                        key={p.v}
                        value={p.v}
                        className="mt-6 space-y-6"
                    >
                        {loading ? (
                            <div className="text-center py-16 text-slate-500">
                                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                            </div>
                        ) : report ? (
                            <>
                                <div
                                    className="surface rounded-2xl p-6"
                                    data-testid={`report-body-${p.v}`}
                                >
                                    <div className="flex items-center gap-2 mb-4">
                                        <Badge
                                            variant="outline"
                                            className="border-cyan-500/30 bg-cyan-500/10 text-cyan-200"
                                        >
                                            {report.period_label}
                                        </Badge>
                                        <span className="text-xs text-slate-500 mono">
                                            {report.window_start?.slice(0, 10)} →{" "}
                                            {report.window_end?.slice(0, 10)}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                                        <MetricPill
                                            label="Avg rating (window)"
                                            value={report.metrics?.avg_rating_window}
                                        />
                                        <MetricPill
                                            label="Avg (all-time)"
                                            value={report.metrics?.avg_rating_all}
                                        />
                                        <MetricPill
                                            label="Reviews (window)"
                                            value={report.metrics?.review_count_window}
                                        />
                                        <MetricPill
                                            label="Reviews (total)"
                                            value={report.metrics?.review_count_all}
                                        />
                                        <MetricPill
                                            label="Open complaints"
                                            value={report.metrics?.open_complaint_count}
                                            accent="rose"
                                        />
                                    </div>
                                    <div className="space-y-5">
                                        {SECTIONS.map((s) => (
                                            <section
                                                key={s.key}
                                                data-testid={`report-section-${s.key}`}
                                            >
                                                <h3 className="text-sm font-bold text-slate-100 tracking-wide uppercase mono">
                                                    {s.label}
                                                </h3>
                                                <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                                                    {report[s.key]}
                                                </p>
                                            </section>
                                        ))}
                                    </div>
                                </div>

                                {/* Ask AI about THIS report */}
                                <div
                                    className="surface rounded-2xl p-6"
                                    data-testid="ask-report-panel"
                                >
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="h-8 w-8 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
                                            <LightbulbIcon className="h-4 w-4 text-indigo-300" />
                                        </div>
                                        <div>
                                            <div className="label-eyebrow text-indigo-300">
                                                Ask AI about this report
                                            </div>
                                            <h3 className="text-lg font-bold text-slate-100">
                                                Grounded on THIS {report.period_label.toLowerCase()} report only
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <Textarea
                                            data-testid="ask-report-input"
                                            rows={2}
                                            value={question}
                                            onChange={(e) => setQuestion(e.target.value)}
                                            placeholder="e.g. Why did open complaints go up this window?"
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                    e.preventDefault();
                                                    ask();
                                                }
                                            }}
                                            className="flex-1 bg-white/[0.02] border-white/5 text-slate-100 placeholder:text-slate-500"
                                        />
                                        <Button
                                            onClick={ask}
                                            disabled={asking || !question.trim()}
                                            data-testid="ask-report-submit"
                                            className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5"
                                        >
                                            {asking ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Sparkles className="h-4 w-4" />
                                            )}
                                            Ask
                                        </Button>
                                    </div>
                                    {answer && (
                                        <div
                                            data-testid="ask-report-answer"
                                            className="mt-4 rounded-xl bg-indigo-500/[0.06] border border-indigo-500/20 p-4 text-sm text-slate-200 whitespace-pre-wrap leading-relaxed"
                                        >
                                            {answer}
                                        </div>
                                    )}
                                </div>

                                {/* Schedule form */}
                                <div
                                    className="surface rounded-2xl p-6 space-y-4"
                                    data-testid="report-schedule-panel"
                                >
                                    <div className="flex items-center gap-2">
                                        <Clock className="h-4 w-4 text-amber-300" />
                                        <div className="label-eyebrow text-amber-300">Delivery schedule</div>
                                    </div>
                                    {scheduleLabel && (
                                        <div className="rounded-xl bg-white/[0.02] border border-amber-500/20 p-3 text-xs text-amber-100/90 flex items-start gap-2">
                                            <Info className="h-3.5 w-3.5 mt-0.5 text-amber-300 shrink-0" />
                                            <span data-testid="schedule-demo-label">
                                                {scheduleLabel}
                                            </span>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <label className="text-xs mono uppercase tracking-widest text-slate-500">
                                                Cadence
                                            </label>
                                            <Select
                                                value={scheduleForm.cadence}
                                                onValueChange={(v) =>
                                                    setScheduleForm((s) => ({ ...s, cadence: v }))
                                                }
                                            >
                                                <SelectTrigger
                                                    className="mt-1 bg-white/[0.03] border-white/10 text-sm text-slate-100"
                                                    data-testid="schedule-cadence"
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {CADENCES.map((c) => (
                                                        <SelectItem key={c.v} value={c.v}>
                                                            {c.l}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-xs mono uppercase tracking-widest text-slate-500">
                                                Recipients (comma-separated)
                                            </label>
                                            <Input
                                                data-testid="schedule-recipients"
                                                value={scheduleForm.recipients}
                                                onChange={(e) =>
                                                    setScheduleForm((s) => ({
                                                        ...s,
                                                        recipients: e.target.value,
                                                    }))
                                                }
                                                placeholder="owner@brightsmile.demo, ops@bistro22.demo"
                                                className="mt-1 bg-white/[0.03] border-white/10 text-slate-100"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Switch
                                                data-testid="schedule-enabled"
                                                checked={scheduleForm.enabled}
                                                onCheckedChange={(v) =>
                                                    setScheduleForm((s) => ({ ...s, enabled: v }))
                                                }
                                            />
                                            <span className="text-sm text-slate-200">
                                                {scheduleForm.enabled ? "Enabled" : "Disabled"}
                                            </span>
                                        </div>
                                        <Button
                                            data-testid="schedule-save"
                                            onClick={saveSchedule}
                                            disabled={savingSched}
                                            className="bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-200 gap-2"
                                        >
                                            {savingSched ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Mail className="h-4 w-4" />
                                            )}
                                            Save schedule
                                        </Button>
                                    </div>
                                </div>

                                {/* Report history */}
                                {history.length > 1 && (
                                    <div
                                        className="surface rounded-2xl p-5"
                                        data-testid="report-history-panel"
                                    >
                                        <div className="flex items-center gap-2 mb-3">
                                            <History className="h-4 w-4 text-slate-400" />
                                            <div className="label-eyebrow">Report history</div>
                                        </div>
                                        <ul className="divide-y divide-white/5">
                                            {history.map((h) => (
                                                <li
                                                    key={h.id}
                                                    data-testid={`report-history-${h.id}`}
                                                    className="flex items-center justify-between py-2 text-sm text-slate-300"
                                                >
                                                    <div>
                                                        <span className="mono text-xs text-slate-500 mr-2">
                                                            {(h.generated_at || "").slice(0, 16).replace("T", " ")}
                                                        </span>
                                                        {h.period_label} · avg rating{" "}
                                                        {h.metrics?.avg_rating_window ?? "—"} · reviews{" "}
                                                        {h.metrics?.review_count_window ?? "—"}
                                                    </div>
                                                    <button
                                                        onClick={() => setReport(h)}
                                                        data-testid={`report-history-open-${h.id}`}
                                                        className="text-indigo-300 hover:text-indigo-200 text-xs flex items-center gap-1"
                                                    >
                                                        Open <ChevronRight className="h-3 w-3" />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-16 text-slate-500 text-sm">
                                No report yet.
                            </div>
                        )}
                    </TabsContent>
                ))}
            </Tabs>

            <WhatsAppReportSection businessId={currentBusiness?.id} />
        </div>
    );
}

function MetricPill({ label, value, accent = "indigo" }) {
    const map = {
        indigo: "border-indigo-500/30 bg-indigo-500/10 text-indigo-100",
        rose: "border-rose-500/30 bg-rose-500/10 text-rose-100",
    };
    return (
        <div className={`rounded-xl border p-3 ${map[accent] || map.indigo}`}>
            <div className="label-eyebrow">{label}</div>
            <div className="mt-1 text-lg font-bold">{value ?? "—"}</div>
        </div>
    );
}
